import React from "react";
import { ChevronsLeftRight } from "lucide-react";

export default function BeforeAfter({ before, after, beforeLabel = "Foto original", afterLabel = "Retrato", aspectRatio = "1 / 1" }) {
  const wrapperRef = React.useRef(null);
  const [pos, setPos] = React.useState(50);
  const [dragging, setDragging] = React.useState(false);

  const updateFromClientX = React.useCallback((clientX) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  }, []);

  React.useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(clientX);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, updateFromClientX]);

  const onDown = (e) => {
    setDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(clientX);
  };

  // Animate handle on mount: sweep from 30 -> 70 -> 50
  React.useEffect(() => {
    let mounted = true;
    const sequence = [30, 70, 50];
    let i = 0;
    const tick = () => {
      if (!mounted) return;
      setPos(sequence[i]);
      i++;
      if (i < sequence.length) setTimeout(tick, 900);
    };
    setTimeout(tick, 600);
    return () => { mounted = false; };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="ba-wrapper cursor-ew-resize"
      style={{ aspectRatio }}
      onMouseDown={onDown}
      onTouchStart={onDown}
      role="slider"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Comparar antes y después"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((v) => Math.max(0, v - 4));
        if (e.key === "ArrowRight") setPos((v) => Math.min(100, v + 4));
      }}
    >
      <img src={before} alt={beforeLabel} className="ba-image" draggable={false} />
      <div className="ba-after" style={{ width: `${pos}%` }}>
        <img
          src={after}
          alt={afterLabel}
          className="ba-image"
          draggable={false}
          style={{ width: `${10000 / pos}%`, maxWidth: "none" }}
        />
      </div>
      <span className="ba-label" style={{ left: 16 }}>{afterLabel}</span>
      <span className="ba-label" style={{ right: 16 }}>{beforeLabel}</span>
      <div className="ba-handle" style={{ left: `${pos}%` }} />
      <div className="ba-knob" style={{ left: `${pos}%` }}>
        <ChevronsLeftRight className="h-5 w-5" />
      </div>
    </div>
  );
}
